import React, { useState } from 'react'
import { Popover2 } from '@blueprintjs/popover2'
import { Spinner, Tag } from '@blueprintjs/core'
import { Button } from '../ui/button'
import { useRecheckProfileLink } from '../../hooks/useRecheckProfileLink'
import Profile = Models.Profile

interface ApiAccessCellProps {
    profile: Profile
    /**
     * Called after a successful re-check that returned an updated profile,
     * so the parent table can mutate the row's `unlinked_at` in place
     * (avoids waiting for a refetch round-trip).
     */
    onAfterRecheck?: (updatedProfile: Profile) => void
}

/**
 * MER-2468: API-access status cell for the data-connections / settings tables.
 *
 * Why a dedicated component (instead of an inline cell render fn):
 *
 * The Table component at components/ui/Table.tsx memoizes its `visibleColumns`
 * with a dep array of `[columns.length, ...]` — by *count*, not by reference.
 * That means when a cell render fn captures changing state via closure (e.g.
 * an `isLoading` flag from a hook), the parent's re-render produces a new
 * `columns` array with new closures, but `columns.length` is unchanged so the
 * memo returns the *old* columns with stale closures. The popover would
 * never see `isLoading` flip to true.
 *
 * Pulling the dynamic state into its own component side-steps this entirely:
 * the cell render fn is stable (always returns `<ApiAccessCell profile=... />`)
 * and React's normal hook-state re-render path drives the popover swap.
 */
export const ApiAccessCell: React.FC<ApiAccessCellProps> = ({ profile, onAfterRecheck }) => {
    const { recheck, isRechecking } = useRecheckProfileLink()
    const isLoading = isRechecking(profile.profile_id)

    // Controlled popover state. We need control so the click on
    // "Re-check connection" can close the popover before/while the
    // request runs (the badge below takes over showing the loading
    // state). Without control, the popover would stay open showing
    // a stale "API access lost..." panel during the in-flight call.
    const [isOpen, setIsOpen] = useState(false)

    if (!profile.unlinked_at) {
        return (
            <Tag intent="success" minimal round icon="tick-circle">
                Connected
            </Tag>
        )
    }

    // While re-checking, the badge becomes the loading indicator —
    // neutral intent + spinner icon + "Checking…" text. Not interactive
    // (no popover open) so the user can't fire a duplicate call.
    if (isLoading) {
        return (
            <Tag intent="none" minimal round icon={<Spinner size={14} />}>
                Checking…
            </Tag>
        )
    }

    const handleClick = async () => {
        // Close the popover synchronously so the user sees an immediate
        // response to their click; the badge swaps to the loading variant
        // above on the next render.
        setIsOpen(false)
        const result = await recheck(profile)
        if (result.profile && onAfterRecheck) {
            onAfterRecheck(result.profile)
        }
    }

    return (
        <Popover2
            isOpen={isOpen}
            onInteraction={(next) => setIsOpen(next)}
            interactionKind="click"
            placement="bottom"
            content={
                <div className="p-3 flex flex-col gap-2" style={{ maxWidth: 320 }}>
                    <div className="text-sm font-semibold">
                        API access lost on {new Date(profile.unlinked_at).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-600">
                        This usually means MerchJar's access was revoked in Amazon Ads, often after
                        a permissions change or accidental disconnect.
                    </div>
                    <div className="text-sm text-gray-600">
                        Data syncs cannot be performed for this profile until it's reconnected.
                    </div>
                    <div className="text-sm text-gray-600">
                        Re-authorize MerchJar in Amazon Ads, then click below to confirm.
                    </div>
                    <Button text="Re-check connection" icon="refresh" onClick={handleClick} />
                </div>
            }
        >
            <Tag interactive intent="danger" minimal round icon="offline">
                Disconnected
            </Tag>
        </Popover2>
    )
}
